import { light_offer_extended } from "@/lib/models/light_offer_extended";
import { offer } from "@/generated";
import { readable_reward } from "@/generated/models/readable_reward";
import isSpecialCoupon from "./isSpecialCoupon";
import is10PercentOffCoupon from "./is10PercentOffCoupon";

export type CouponValidationError =
  | "max_coupons_limit_reached"
  | "special_day_coupon_conflict"
  | "10_percent_off_coupon_conflict"
  | "multiple_special_day_coupons"
  | "multiple_10_percent_off_coupons";

export interface CouponValidationResult {
  isValid: boolean;
  error?: CouponValidationError;
}

/**
 * Validates whether a coupon can be clipped/added to the basket
 * @param offerToAdd - The offer to be added
 * @param basket - Current basket items
 * @param gachaOffers - Current gacha offers (optional)
 * @returns Validation result with error type if invalid
 */
export function validateCouponClip(
  offerToAdd: offer | light_offer_extended,
  basket: readonly readable_reward[],
  gachaOffers: readonly light_offer_extended[] = [],
): CouponValidationResult {
  // Count currently kept gacha offers
  const keptGachaCount = gachaOffers.filter(
    (o) => o.keep === 1 && o.used === 0,
  ).length;
  const totalKeptOffers = basket.length + keptGachaCount;

  // Validation: Check if total kept offers would exceed 5
  if (totalKeptOffers >= 5) {
    return {
      isValid: false,
      error: "max_coupons_limit_reached",
    };
  }

  // Check for existing coupon types in basket
  const hasSpecialDayCoupon = basket.some((item) =>
    item.offer ? isSpecialCoupon(item.offer) : false,
  );

  const has10PercentOffCoupon = basket.some((item) =>
    item.offer ? is10PercentOffCoupon(item.offer) : false,
  );

  // Check if trying to add special day coupon when basket already has one
  if (isSpecialCoupon(offerToAdd) && hasSpecialDayCoupon) {
    return {
      isValid: false,
      error: "multiple_special_day_coupons",
    };
  }

  // Check if trying to add 10% off coupon when basket already has one
  if (is10PercentOffCoupon(offerToAdd) && has10PercentOffCoupon) {
    return {
      isValid: false,
      error: "multiple_10_percent_off_coupons",
    };
  }

  // Check if trying to add special day coupon when 10% off exists
  if (isSpecialCoupon(offerToAdd) && has10PercentOffCoupon) {
    return {
      isValid: false,
      error: "special_day_coupon_conflict",
    };
  }

  // Check if trying to add 10% off coupon when special day exists
  if (is10PercentOffCoupon(offerToAdd) && hasSpecialDayCoupon) {
    return {
      isValid: false,
      error: "10_percent_off_coupon_conflict",
    };
  }

  return {
    isValid: true,
  };
}
